import { AriaAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton, Stack } from '@mui/material';

import clsx from 'clsx';

import DateWidget from '../../../components/DateWidget';
import Typography from '../../../components/Typography';
import { i18nNS } from '../../../i18n';
import { Activities, ClassGrid, classCardClasses, Root, StyledTag, StyledZoomUsIcon } from './styles';
import useClassCardVM, { Props as VMProps } from './vm';

interface Props extends VMProps, AriaAttributes {
  className?: string;
  classes?: Partial<typeof classCardClasses>;
  showActivitiesCount?: boolean;
}

const ClassCard = ({ course, cls, className, classes, ...restProps }: Props) => {
  const { t } = useTranslation([i18nNS.CLASS, i18nNS.COMMON, i18nNS.GLOSSARY]);

  const { classWidgetData, isClassInSession, isClassClosed } = useClassCardVM({
    course,
    cls,
  });

  if (!classWidgetData) return <Root className={className} />;

  const { variant, date, timing } = classWidgetData;

  return (
    <Root
      {...restProps}
      className={clsx(
        classCardClasses.root,
        className,
        classCardClasses[variant],
        classes?.root,
        classes?.[variant]
      )}
    >
      <ClassGrid className={clsx(classCardClasses.classGrid, classes?.classGrid)}>
        <DateWidget
          variant={variant}
          date={date}
          className={clsx(classCardClasses.dateWidget, classes?.dateWidget)}
        />
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          className={clsx(classCardClasses.widgetType, classes?.widgetType)}
        >
          {course?.isPro && (
            <StyledZoomUsIcon className={clsx(classCardClasses.zoomUsIcon, classes?.zoomUsIcon)} />
          )}
          {isClassInSession ? (
            <StyledTag
              variant="filled"
              color="success"
              label={t('in_session', { ns: i18nNS.GLOSSARY })}
              className={clsx(classCardClasses.inSessionTag, classes?.inSessionTag)}
            />
          ) : isClassClosed ? (
            <StyledTag
              variant="filled"
              color="default"
              label={t('completed', { ns: i18nNS.GLOSSARY })}
              className={clsx(classCardClasses.completedTag, classes?.completedTag)}
            />
          ) : (
            <Typography
              variant="titleBold"
              fontSize={10}
              color="primary.900"
              className={clsx(classCardClasses.activityName, classes?.activityName)}
            >
              {t('class', { ns: i18nNS.GLOSSARY })}
            </Typography>
          )}
        </Stack>
        <Typography
          variant="h6Bold"
          component="h5"
          color="grey.900"
          className={clsx(classCardClasses.timings, classes?.timings)}
        >
          {timing}
        </Typography>
        <Typography
          variant="h6Medium"
          component="h5"
          color="grey.700"
          className={clsx(classCardClasses.title, classes?.title)}
        >
          {t('class', { ns: i18nNS.GLOSSARY })}
        </Typography>
      </ClassGrid>
      <Activities className={clsx(classCardClasses.activities, classes?.activities)}>
        {new Array(5).fill('').map((_, index) => (
          <Skeleton key={index} variant="rounded" height={26} width={26} />
        ))}
      </Activities>
    </Root>
  );
};

export default ClassCard;
